import {CreditCard, ListChecks, ShieldCheck, Users} from 'lucide-react';
import type {ActionableConditionKind} from 'components/RemoteAccess/lockConditions';
import {VelaScene, type VelaContext, type VelaRule} from '../engine/types';

export const KEYS_LAST_STEP_CARD_ID = 'keys-last-step';

const LAST_STEP_ICON: Record<ActionableConditionKind, JSX.Element> = {
  payment: <CreditCard />,
  registration: <Users />,
  verification: <ShieldCheck />,
  checklist: <ListChecks />,
};

const LAST_STEP_COPY: Record<ActionableConditionKind, {title: string; body: string}> = {
  payment: {
    title: 'card.keys_last_step.payment.title',
    body: 'card.keys_last_step.payment.body',
  },
  registration: {
    title: 'card.keys_last_step.registration.title',
    body: 'card.keys_last_step.registration.body',
  },
  verification: {
    title: 'card.keys_last_step.verification.title',
    body: 'card.keys_last_step.verification.body',
  },
  checklist: {
    title: 'card.keys_last_step.checklist.title',
    body: 'card.keys_last_step.checklist.body',
  },
};

type KeysQuestion = {
  id: string;
  priority: number;
  isShown: (ctx: VelaContext) => boolean;
  questionKey: string;
  answerKey: string | ((ctx: VelaContext) => string);
};

const isGated = (ctx: VelaContext) => ctx.keysState === 'gated';
const hasKeys = (ctx: VelaContext) => ctx.keysState !== 'gated';
const isLive = (ctx: VelaContext) => ctx.keysState === 'live';

const KEYS_QUESTIONS: KeysQuestion[] = [
  {
    id: 'how-to-get',
    priority: 100,
    isShown: isGated,
    questionKey: 'chat.keys.how_to_get.q',
    answerKey: ctx =>
      ctx.blockingKeyCondition
        ? 'chat.keys.how_to_get.a'
        : 'card.nothing_left_to_do.body',
  },
  {
    id: 'when-appear',
    priority: 75,
    isShown: isGated,
    questionKey: 'chat.keys.when_appear.q',
    answerKey: 'chat.keys.when_appear.a',
  },
  {
    id: 'code-fails',
    priority: 95,
    isShown: ctx => hasKeys(ctx) && ctx.hasCodeKeys,
    questionKey: 'chat.keys.code_fails.q',
    answerKey: 'chat.keys.code_fails.a',
  },
  {
    id: 'how-to-open',
    priority: 92,
    isShown: ctx => hasKeys(ctx) && ctx.hasLinkKeys,
    questionKey: 'chat.keys.how_to_open.q',
    answerKey: 'chat.keys.how_to_open.a',
  },
  {
    id: 'slide-fails',
    priority: 70,
    isShown: ctx => isLive(ctx) && ctx.hasLinkKeys,
    questionKey: 'card.nothing_happens_when_i_slide.title',
    answerKey: 'card.nothing_happens_when_i_slide.body',
  },
  {
    id: 'when-work',
    priority: 60,
    isShown: hasKeys,
    questionKey: 'card.when_do_my_keys_work.title',
    answerKey: ctx =>
      isLive(ctx)
        ? 'card.when_do_my_keys_work.body'
        : 'card.access_opens_at_check_in.body',
  },
  {
    id: 'need-in-earlier',
    priority: 55,
    isShown: ctx => ctx.keysState === 'waiting',
    questionKey: 'card.need_in_earlier.title',
    answerKey: 'card.need_in_earlier.body',
  },
  {
    id: 'stuck',
    priority: 50,
    isShown: () => true,
    questionKey: 'chat.keys.stuck.q',
    answerKey: 'chat.keys.stuck.a',
  },
  {
    id: 'lock-up',
    priority: 45,
    isShown: ctx => isLive(ctx) && ctx.hasLinkKeys,
    questionKey: 'card.do_i_need_to_lock_up.title',
    answerKey: 'card.do_i_need_to_lock_up.body',
  },
  {
    id: 'share-code',
    priority: 40,
    isShown: ctx => hasKeys(ctx) && ctx.hasCodeKeys,
    questionKey: 'chat.keys.share_code.q',
    answerKey: 'chat.keys.share_code.a',
  },
];

export const remoteAccessRules: VelaRule[] = [
  {
    id: 'keys/last-step',
    section: 'whyYouCantStart',
    scenes: [VelaScene.RemoteAccess],
    priority: 30,
    evaluate: (ctx, t) =>
      ctx.keysState === 'gated' && ctx.blockingKeyCondition
        ? {
            id: KEYS_LAST_STEP_CARD_ID,
            icon: LAST_STEP_ICON[ctx.blockingKeyCondition],
            title: t(LAST_STEP_COPY[ctx.blockingKeyCondition].title),
            description: t(LAST_STEP_COPY[ctx.blockingKeyCondition].body),
          }
        : null,
  },
  ...KEYS_QUESTIONS.map(
    ({id, isShown, questionKey, answerKey, priority}): VelaRule => ({
      id: `keys/ask-${id}`,
      section: 'askVela',
      scenes: [VelaScene.RemoteAccess],
      priority,
      evaluate: (ctx, t) =>
        isShown(ctx)
          ? {
              id: `keys-${id}`,
              title: t(questionKey),
              description: t(
                typeof answerKey === 'function' ? answerKey(ctx) : answerKey,
              ),
            }
          : null,
    }),
  ),
];
